import { MouseEventHandler } from "react";
import styled from "styled-components";
import { GenericSvg, SVGName } from "../../assets/svgs/GenericSVG";
import { colors } from "../../styles/colors";
import { TextButtonText } from "../../styles/stringStyles";
import { LoadingSpinner } from "../loading/LoadingSpinner";
import { CustomButton } from "./CustomButton";

export type TextButtonProps = {
  text: string;
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  className?: string;
  disabled?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
  loading?: boolean;
  noUnderline?: boolean;
  color?: string;
  noPadding?: boolean;
  icon?: {
    name: SVGName;
    size?: number;
    color?: string;
    secondaryColor?: string;
  };
  secondIcon?: {
    name: SVGName;
    size?: number;
    color?: string;
  };
};

export const TextButton = (props: TextButtonProps) => {
  return (
    <StyledCustomButton
      ref={props.ref}
      onClick={props.onClick}
      disabled={props.disabled || props.loading}
      className={props.className}>
      <ContentContainer>
        <LoadingContainer $loading={props.loading}>
          <LoadingSpinner color={props.color ?? colors.dark} />
        </LoadingContainer>
        <ButtonContent $loading={props.loading} $noPadding={props.noPadding}>
          {props.icon && (
            <GenericSvg
              name={props.icon.name}
              size={props.icon.size ?? 24}
              primaryColor={props.icon.color ?? colors.dark}
              secondaryColor={props.icon.secondaryColor ?? "transparent"}
            />
          )}
          <StyledTextButtonText
            $noUnderline={props.noUnderline}
            $color={props.color}>
            {props.text}
          </StyledTextButtonText>
          {props.secondIcon && (
            <GenericSvg
              name={props.secondIcon.name}
              size={props.secondIcon.size ?? 24}
              primaryColor={props.secondIcon.color ?? colors.dark}
            />
          )}
        </ButtonContent>
      </ContentContainer>
    </StyledCustomButton>
  );
};

const StyledCustomButton = styled(CustomButton)`
  width: fit-content;
  position: relative;
`;

const ContentContainer = styled.div`
  position: relative;
`;

const ButtonContent = styled.div<{ $loading?: boolean; $noPadding?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${(props) => (props.$noPadding ? "14px 0" : "14px 20px")};
  gap: 5px;
  visibility: ${(props) => (props.$loading ? "hidden" : "visible")};
  width: 100%;
`;

const LoadingContainer = styled.div<{ $loading?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  visibility: ${(props) => (props.$loading ? "visible" : "hidden")};
`;

const StyledTextButtonText = styled(TextButtonText)<{
  $noUnderline?: boolean;
  $color?: string;
}>`
  width: 100%;
  text-align: left;
  text-decoration: ${(props) => (props.$noUnderline ? "none" : "underline")};
  text-decoration-color: ${(props) =>
    props.$color ? props.$color : colors.dark};
  color: ${(props) => (props.$color ? props.$color : colors.dark)};
`;
